import { useEffect, useRef } from 'react';
import { type Subscription, filter, fromEvent, tap } from 'rxjs';

import { useSubscriptions } from '../rxjs';

export const useDropRef = <T extends HTMLElement>(
  ref: React.RefObject<Nullish<T>>,
  onDrop: (event: DragEvent) => void,
  deps: any[] = [],
) => {
  useEffect(() => {
    if (!ref.current) return;

    ref.current.draggable = true;
  }, []);

  useSubscriptions(
    () =>
      ref.current
        ? [
            fromEvent<DragEvent>(ref.current, 'dragover')
              .pipe(tap(event => event.preventDefault()))
              .subscribe(),

            fromEvent<DragEvent>(ref.current, 'drop')
              .pipe(
                filter(() => ref.current != null),
                tap(event => {
                  event.preventDefault();
                  event.stopPropagation();
                  onDrop(event);
                }),
              )
              .subscribe(),
          ]
        : [],
    [...deps],
  );
};

/**
 * TODO: 임시로 처방한 코드. 추후 수정 필요함
 */
export const useDrop = (onDrop: (event: DragEvent) => void) => {
  const subscriptionListRef = useRef<Subscription[]>([]);
  const onDropRef = useRef(onDrop);
  const targetRef = useRef<HTMLElement>(null);

  const connect = <T extends HTMLElement>(element: T) => {
    if (!element) return;

    targetRef.current = element;

    subscriptionListRef.current.push(
      fromEvent<DragEvent>(targetRef.current, 'dragover')
        .pipe(tap(event => event.preventDefault()))
        .subscribe(),
    );

    subscriptionListRef.current.push(
      fromEvent<DragEvent>(targetRef.current, 'drop')
        .pipe(
          filter(() => targetRef.current != null),
          tap(event => {
            event.preventDefault();
            event.stopPropagation();
            onDropRef.current(event);
          }),
        )
        .subscribe(),
    );
  };

  const disconnect = () => {
    subscriptionListRef.current.forEach(sub => sub.unsubscribe());
    subscriptionListRef.current = [];
  };

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []);

  useEffect(() => {
    onDropRef.current = onDrop;
  }, [onDrop]);

  return { connect, disconnect };
};
